"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { ChevronRight, Star } from "lucide-react";
import { useFavorieten } from "@/components/favorieten";
import { MiniTabel, type MiniRij } from "@/components/home/mini-tabel";

export type TabUitslag = {
  wedstrijdId: number;
  wedstrijdNaam: string;
  /** Al opgemaakt op de server, zodat hier geen Date overheen hoeft. */
  datumTekst: string;
  klasseNaam: string;
  /** Naam van het gevolgde team, als díe bepaalde welke uitslag hier staat. */
  omdat: string | null;
  rijen: MiniRij[];
};

export type TabStand = {
  jaar: number;
  klasseNaam: string;
  /** Naam van het gevolgde team, als dát de reden is dat deze klasse er staat. */
  omdat: string | null;
  rijen: MiniRij[];
};

type Tab = "uitslag" | "stand";

// Uitslagen en standen delen één blok met twee tabbladen.
//
// Béide gaan over hetzelfde team: de laatste wedstrijd waar je team reed, en de
// stand van de klasse waarin het rijdt. Volg je niemand, dan is het de laatst
// gereden wedstrijd van de NTTO met de stand van diezelfde klasse ernaast.
//
// De favorieten staan op het apparaat, dus de server kan dat niet vooruit
// weten. Hij levert de algemene versie mee, en zodra de opslag gelezen is
// wisselen we om naar die van jouw team. Tot die tijd staat er dus al iets, en
// geen laadbalk.
export function UitslagStand({
  uitslag,
  stand,
}: {
  uitslag: TabUitslag | null;
  stand: TabStand | null;
}) {
  const [tab, setTab] = useState<Tab>("uitslag");
  const { teams: gevolgdeTeams, geladen } = useFavorieten();
  const [eigen, setEigen] = useState<{ uitslag: TabUitslag | null; stand: TabStand | null } | null>(
    null,
  );

  useEffect(() => {
    if (!geladen || gevolgdeTeams.length === 0) return;

    const afbreken = new AbortController();
    fetch("/api/stand", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(gevolgdeTeams.map((f) => ({ sectie: f.sectie, id: f.id }))),
      signal: afbreken.signal,
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d: { uitslag: TabUitslag | null; stand: TabStand | null } | null) => setEigen(d))
      .catch(() => {
        // Netwerkfout: dan blijft staan wat de server al meegaf.
      });

    return () => afbreken.abort();
  }, [gevolgdeTeams, geladen]);

  // Volg je niemand meer, dan telt wat we eerder ophaalden niet langer. Dat
  // hier afleiden in plaats van in het effect leegmaken: dan hoeft er geen
  // extra render overheen.
  const vanEigenTeam = gevolgdeTeams.length > 0 ? eigen : null;
  const teTonenUitslag = vanEigenTeam?.uitslag ?? uitslag;
  const teTonen = vanEigenTeam?.stand ?? stand;

  if (!teTonenUitslag && !teTonen) {
    return (
      <p className="px-4 py-6 text-center text-sm text-muted-foreground">
        Nog geen uitslagen beschikbaar.
      </p>
    );
  }

  return (
    <div className="px-4">
      <div role="tablist" aria-label="Uitslagen en standen" className="mb-3 flex gap-1">
        <TabKnop actief={tab === "uitslag"} onClick={() => setTab("uitslag")} id="tab-uitslag">
          Laatste uitslag
        </TabKnop>
        <TabKnop actief={tab === "stand"} onClick={() => setTab("stand")} id="tab-stand">
          Stand
        </TabKnop>
      </div>

      <div
        role="tabpanel"
        id="paneel-uitslag"
        aria-labelledby="tab-uitslag"
        hidden={tab !== "uitslag"}
      >
        {teTonenUitslag ? (
          <>
            <Link
              href={`/uitslagen/${teTonenUitslag.wedstrijdId}`}
              className="mb-3 flex items-center justify-between gap-3 rounded-xl bg-card px-3 py-2.5 transition-colors active:bg-[#24456b]"
            >
              <span className="min-w-0">
                <span className="block truncate font-display text-sm font-black uppercase tracking-tight text-white">
                  {teTonenUitslag.wedstrijdNaam}
                </span>
                <span className="block truncate text-xs capitalize text-muted-foreground">
                  {teTonenUitslag.datumTekst} · {teTonenUitslag.klasseNaam}
                </span>
                {teTonenUitslag.omdat && (
                  <span className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Star className="size-3 shrink-0 fill-primary text-primary" aria-hidden />
                    <span className="truncate">omdat je {teTonenUitslag.omdat} volgt</span>
                  </span>
                )}
              </span>
              <ChevronRight className="size-5 shrink-0 text-primary" aria-hidden />
            </Link>
            <MiniTabel rijen={teTonenUitslag.rijen} waardeKop="Afstand" />
          </>
        ) : (
          <p className="py-6 text-center text-sm text-muted-foreground">Nog geen uitslagen beschikbaar.</p>
        )}
      </div>

      <div role="tabpanel" id="paneel-stand" aria-labelledby="tab-stand" hidden={tab !== "stand"}>
        {teTonen ? (
          <>
            <Link
              href="/uitslagen?tab=standen"
              className="mb-3 flex items-center justify-between gap-3 rounded-xl bg-card px-3 py-2.5 transition-colors active:bg-[#24456b]"
            >
              <span className="min-w-0">
                <span className="block truncate font-display text-sm font-black uppercase tracking-tight text-white">
                  {teTonen.klasseNaam}
                </span>
                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                  {teTonen.omdat ? (
                    <>
                      <Star className="size-3 shrink-0 fill-primary text-primary" aria-hidden />
                      <span className="truncate">omdat je {teTonen.omdat} volgt</span>
                    </>
                  ) : (
                    <span className="truncate">seizoen {teTonen.jaar}</span>
                  )}
                </span>
              </span>
              <ChevronRight className="size-5 shrink-0 text-primary" aria-hidden />
            </Link>
            <MiniTabel rijen={teTonen.rijen} waardeKop="Punten" />
          </>
        ) : (
          <p className="py-6 text-center text-sm text-muted-foreground">Nog geen stand beschikbaar.</p>
        )}
      </div>
    </div>
  );
}

function TabKnop({
  actief,
  onClick,
  id,
  children,
}: {
  actief: boolean;
  onClick: () => void;
  id: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      id={id}
      aria-selected={actief}
      aria-controls={id === "tab-uitslag" ? "paneel-uitslag" : "paneel-stand"}
      onClick={onClick}
      className={`flex-1 rounded-lg px-3 py-2 font-display text-xs font-black uppercase tracking-wide transition-colors ${
        actief
          ? "bg-primary text-black"
          : "bg-card text-soft-foreground active:bg-[#24456b]"
      }`}
    >
      {children}
    </button>
  );
}
